import axios from 'axios';

// Use server proxy to avoid CORS and hide API key
const SERVER_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5002';

// Create isolated axios instance to avoid global interceptors (which may log the user out on errors)
const http = axios.create({
  baseURL: SERVER_BASE,
  withCredentials: false,
  headers: {
    // Ensure no auth headers are sent; weather endpoints are public
  }
});

/**
 * Weather Service - Handles API calls to OpenWeatherMap
 */
const weatherService = {
  /**
   * Get current weather by location name
   * @param {string} location - City name, state code (optional), country code (optional)
   * @returns {Promise} - Weather data promise
   */
  getCurrentWeather: async (location) => {
    try {
      const response = await http.get(`/public/weather`, {
        params: {
          q: location,
          units: 'imperial'
        }
      });
      
      return {
        location: response.data.name,
        condition: response.data.weather[0].main,
        description: response.data.weather[0].description,
        temperature: Math.round(response.data.main.temp),
        feelsLike: Math.round(response.data.main.feels_like),
        humidity: response.data.main.humidity,
        windSpeed: Math.round(response.data.wind.speed),
        windDirection: response.data.wind.deg,
        sunrise: new Date(response.data.sys.sunrise * 1000).toLocaleTimeString(),
        sunset: new Date(response.data.sys.sunset * 1000).toLocaleTimeString(),
        icon: response.data.weather[0].icon,
        iconUrl: `https://openweathermap.org/img/wn/${response.data.weather[0].icon}@2x.png`,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error fetching weather data:', error);
      throw error;
    }
  },
  
  /**
   * Get 5-day forecast by location name
   * @param {string} location - City name, state code (optional), country code (optional)
   * @returns {Promise} - Forecast data promise
   */
  getForecast: async (location) => {
    try {
      const response = await http.get(`/public/weather/forecast`, {
        params: {
          q: location,
          units: 'imperial',
          cnt: 40
        }
      });
      
      // Group forecast data by day
      const forecastByDay = {};
      
      response.data.list.forEach(item => {
        const date = new Date(item.dt * 1000).toLocaleDateString();
        
        if (!forecastByDay[date]) {
          forecastByDay[date] = [];
        }
        
        forecastByDay[date].push({
          time: new Date(item.dt * 1000).toLocaleTimeString(),
          temperature: Math.round(item.main.temp),
          condition: item.weather[0].main,
          description: item.weather[0].description,
          icon: item.weather[0].icon,
          iconUrl: `https://openweathermap.org/img/wn/${item.weather[0].icon}@2x.png`,
          windSpeed: Math.round(item.wind.speed),
          humidity: item.main.humidity
        });
      });
      
      return {
        location: response.data.city.name,
        country: response.data.city.country,
        forecast: forecastByDay,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error fetching forecast data:', error);
      throw error;
    }
  },
  
  /**
   * Get weather by coordinates
   * @param {number} lat - Latitude
   * @param {number} lon - Longitude
   * @returns {Promise} - Weather data promise
   */
  getWeatherByCoords: async (lat, lon) => {
    try {
      const response = await http.get(`/public/weather`, {
        params: {
          lat,
          lon,
          units: 'imperial'
        }
      });
      
      return {
        location: response.data.name,
        condition: response.data.weather[0].main,
        description: response.data.weather[0].description,
        temperature: Math.round(response.data.main.temp),
        feelsLike: Math.round(response.data.main.feels_like),
        humidity: response.data.main.humidity,
        windSpeed: Math.round(response.data.wind.speed),
        windDirection: response.data.wind.deg,
        sunrise: new Date(response.data.sys.sunrise * 1000).toLocaleTimeString(),
        sunset: new Date(response.data.sys.sunset * 1000).toLocaleTimeString(),
        icon: response.data.weather[0].icon,
        iconUrl: `https://openweathermap.org/img/wn/${response.data.weather[0].icon}@2x.png`,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error fetching weather data by coordinates:', error);
      throw error;
    }
  },
  
  /**
   * Format weather data for display in notes
   * @param {Object} weatherData - Weather data object
   * @returns {string} - Formatted weather notes
   */
  formatWeatherNotes: (weatherData) => {
    if (!weatherData) return '';
    
    const date = new Date(weatherData.timestamp).toLocaleDateString();
    
    return `${date} - ${weatherData.location}: ${weatherData.temperature}°F, ${weatherData.condition}. ` +
           `Wind: ${weatherData.windSpeed} mph. Humidity: ${weatherData.humidity}%. ` +
           `Sunrise: ${weatherData.sunrise}, Sunset: ${weatherData.sunset}.`;
  },

  /**
   * Format a single forecast slot for notes (e.g., selecting tomorrow's time)
   * @param {Object} slot - Forecast time slot with fields: time, temperature, condition, description, windSpeed, humidity
   * @param {string} location - Human-readable location name (e.g., city)
   * @param {string} dateKey - Date string used in grouping (local date)
   * @returns {string}
   */
  formatForecastNotes: (slot, location, dateKey) => {
    if (!slot) return '';
    // dateKey is already in local date format from grouping
    const timeLabel = slot.time?.split(' ')[0] || '';
    const cond = slot.condition || slot.description || '';
    const temp = typeof slot.temperature === 'number' ? `${slot.temperature}°F` : '';
    const wind = typeof slot.windSpeed === 'number' ? `${slot.windSpeed} mph` : 'n/a';
    const humidity = typeof slot.humidity === 'number' ? `${slot.humidity}%` : 'n/a';
    return `${dateKey} ${timeLabel} - ${location}: ${temp}, ${cond}. Wind: ${wind}. Humidity: ${humidity}.`;
  }
};

export default weatherService;
